import { useQuery } from '@tanstack/react-query';
import React, { useEffect, useRef, useState } from 'react';
import { v4 } from 'uuid';

import { AlfredFooter } from '~/components/alfred-footer/AlfredFooter';
import { RenderFeatureFlag } from '~/components/render-feature-flag/RenderFeatureFlag';
import { useLogRootViewHtml } from '~/hooks/use-log-root-view-html/useLogRootViewHtml';
import { postContentQuestions } from '~/services/content-questions/content-questions-service';
import { useAlfredFooterStore } from '~/stores/use-alfred-footer/useAlfredFooter';
import { useChatInputStore } from '~/stores/use-chat-input/useChatInput';
import { FeatureFlag } from '~/stores/use-flags/useFlags';

import * as S from './WithFooterWrapper.styles';

const ROTATION_DURATION_MS = 10000;
const QUERY_KEY_CONTENT_QUESTIONS = 'QUERY_KEY_CONTENT_QUESTIONS';

export interface WithFooterWrapperProps {
  children?: React.ReactNode;
  flag?: FeatureFlag;
  fallback?: React.ReactNode;
  testId?: string;
  isWrappedComponentOpened?: boolean;
  className?: string;
}

export const WithFooterWrapper = ({
  children,
  flag = 'alfred_everywhere_feature',
  fallback = null,
  testId,
  isWrappedComponentOpened = true,
  className,
}: WithFooterWrapperProps) => {
  const contentRef = useRef<HTMLElement | null>(null);
  const rotationIntervalRef = useRef<ReturnType<typeof setInterval> | null>(
    null
  );
  const [questions, setQuestions] = useState<string[]>([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isFooterHovered, setIsFooterHovered] = useState(false);
  const [queryCounter, setQueryCounter] = useState(0);
  const { textContent, reloadTrigger } = useLogRootViewHtml(contentRef);
  const { hasUserInteracted, setHasUserInteracted } = useChatInputStore(
    state => ({
      hasUserInteracted: state.hasUserInteracted,
      setHasUserInteracted: state.setHasUserInteracted,
    })
  );

  const idRef = useRef<string>('');

  if (!idRef.current) {
    idRef.current = v4();
  }

  const { activeId, push, remove } = useAlfredFooterStore(state => ({
    activeId: state.activeId,
    push: state.push,
    remove: state.remove,
  }));

  useEffect(() => {
    if (isWrappedComponentOpened) {
      push(idRef.current);
      return () => remove(idRef.current);
    }

    return undefined;
  }, [isWrappedComponentOpened, push, remove]);

  const isFooterActive = activeId === idRef.current;

  const { data, isLoading } = useQuery({
    queryKey: [QUERY_KEY_CONTENT_QUESTIONS, textContent, queryCounter],
    queryFn: ({ signal }) => postContentQuestions(textContent, signal),
    enabled:
      !!textContent &&
      !hasUserInteracted &&
      isWrappedComponentOpened &&
      isFooterActive,
  });

  useEffect(() => {
    setHasUserInteracted(false);
  }, []);

  useEffect(() => {
    const next = data?.questions ?? [];

    if (next.length) {
      setQuestions(next);
      setCurrentIndex(0);
    } else if (!textContent) {
      setQuestions([]);
      setCurrentIndex(0);
    } else {
      setCurrentIndex(0);
    }
  }, [data, textContent]);

  useEffect(() => {
    if (
      !questions.length ||
      hasUserInteracted ||
      isFooterHovered ||
      !isFooterActive
    ) {
      return;
    }

    rotationIntervalRef.current = setInterval(() => {
      setCurrentIndex(prev => (prev + 1) % questions.length);
    }, ROTATION_DURATION_MS);

    return () => {
      rotationIntervalRef.current && clearInterval(rotationIntervalRef.current);
    };
  }, [questions, hasUserInteracted, isFooterHovered, isFooterActive]);

  const onReloadQuestion = () => {
    setHasUserInteracted(false);
    reloadTrigger();
    rotationIntervalRef.current && clearInterval(rotationIntervalRef.current);
    setCurrentIndex(0);
    setQuestions([]);
    setQueryCounter(prev => prev + 1);
  };

  const onSubmitFromChat = () => {
    rotationIntervalRef.current && clearInterval(rotationIntervalRef.current);

    const nextQuestions = questions.filter((_, idx) => idx !== currentIndex);
    const nextIndex = currentIndex >= nextQuestions.length ? 0 : currentIndex;
    setHasUserInteracted(false);
    setQuestions(nextQuestions);
    setCurrentIndex(nextIndex);

    if (!nextQuestions.length) {
      onReloadQuestion();
    }
  };

  const question = questions[currentIndex];

  return (
    <S.Root data-testid={testId}>
      <S.Content ref={contentRef} className={className}>{children}</S.Content>
      {isWrappedComponentOpened && (
        <RenderFeatureFlag requiredFlag={flag} fallback={fallback}>
          <AlfredFooter
            suggestedQuestion={isFooterActive ? question : undefined}
            progressDurationMs={ROTATION_DURATION_MS}
            progressResetKey={question}
            isActive={isFooterActive}
            disableGradient={
              !questions.length ||
              hasUserInteracted ||
              isLoading ||
              !isFooterActive
            }
            onHoverChange={setIsFooterHovered}
            onReload={onReloadQuestion}
            isLoading={isLoading}
            submitCallback={onSubmitFromChat}
          />
        </RenderFeatureFlag>
      )}
    </S.Root>
  );
};
